import React, { Component } from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { SubmissionError } from 'redux-form'
import QS from 'qs'

import { getApp, getApps, updateApp, requestApp, deleteApp } from 'ducks/apps'
import { openAccordion } from 'ducks/accordions'
import { getFeatureFlag } from 'ducks/featureFlags'
import { getCurrentOrganizationId } from 'ducks/organizations'

import Page from 'components/Shared/Page'
import Button from 'components/Shared/Button'
import Icon from 'components/Shared/Icon'
import Item from 'components/Shared/Panel/Item'

import { fetchApiKey } from 'ducks/apikeys'
import {
  getThirdPartyApiKeys,
  deleteThirdPartyApiKey,
  createThirdPartyApiKey,
  updateThirdPartyApiKey,
} from 'ducks/thirdPartyApiKeys'

import withLocationDataType from 'components/Misc/withLocationDataType'
import AppUsers from './AppUsers'
import AppSettingsForm from './AppSettingsForm'
import DisplaySettings from './DisplaySettings'
import Cloneable from './AppUsers/Cloneable'
import ApiKey from './AppUsers/ApiKey'
import ThirdPartyApiKeys from './ThirdPartyApiKeys'

import './Settings.css'

const GROUP = 'settings'

class Settings extends Component {
  handleDelete = async () => {
    const {
      match,
      history,
      app,
      deleteApp,
      currentOrgId,
      apps,
      hasTeamSwitcher,
    } = this.props

    const { appId } = match.params

    if (!app) {
      return
    }

    if (!window.confirm(`Are you sure you want to delete ${app.name}`)) {
      return
    }

    await deleteApp(appId)

    let url = '/'

    if (currentOrgId && apps && hasTeamSwitcher) {
      const filteredApps = apps.filter(app => app.id !== appId)

      const lastApp = filteredApps.find(
        app => app.Organization.id === currentOrgId
      )

      if (lastApp) {
        url = `/apps/${lastApp.id}`
      } else {
        url = `/first-app-setup`
      }
    }

    history.push(url)
  }

  handleAppSettingsSubmit = data => {
    const { match, updateApp } = this.props
    const { appId } = match.params

    updateApp(appId, data)
  }

  handleAPIKeysSubmit = async data => {
    const {
      match,
      deleteThirdPartyApiKey,
      createThirdPartyApiKey,
      updateThirdPartyApiKey,
      thirdPartyApiKeys,
    } = this.props

    const { appId } = match.params

    for (const type of Object.keys(data)) {
      const { key, id: keyId } = data[type]

      if (key) {
        if (thirdPartyApiKeys.find(({ id }) => id === keyId)) {
          try {
            await updateThirdPartyApiKey(appId, keyId, key)
          } catch ({ error }) {
            throw new SubmissionError({ google: { key: error } })
          }
        } else {
          try {
            await createThirdPartyApiKey(appId, { type, key })
          } catch ({ error }) {
            throw new SubmissionError({ google: { key: error } })
          }
        }
      } else {
        try {
          await deleteThirdPartyApiKey(appId, keyId)
        } catch ({ error }) {
          throw new SubmissionError({ google: { key: error } })
        }
      }
    }
  }

  getInitialAppSettingsData = () => {
    const { app } = this.props
    const { name, icon, mixpanelToken, OrganizationId, description } = app
    let { showBranding } = app

    // this should be enabled by default,
    // especially if a user isn't a paying customer yet
    if (showBranding === undefined) {
      showBranding = true
    }

    return {
      name,
      icon,
      mixpanelToken,
      OrganizationId,
      description,
      showBranding,
    }
  }

  getInitialAPIKeysData = () => {
    const { thirdPartyApiKeys = [] } = this.props

    const hash = {}

    for (const thirdPartyApiKey of thirdPartyApiKeys) {
      const { type, key, id } = thirdPartyApiKey
      hash[type] = { key, id }
    }

    return hash
  }

  setupAccordion() {
    const { history, openAccordion } = this.props

    const { active } = QS.parse(history.location.search, {
      ignoreQueryPrefix: true,
    })

    switch (active) {
      case 'display':
        return openAccordion(GROUP, 'Display')
      case 'access':
        return openAccordion(GROUP, 'Access')
      case 'copydelete':
        return openAccordion(GROUP, 'CopyDelete')
      case 'apikeys':
        return openAccordion(GROUP, 'APIKeys')
      default:
        return openAccordion(GROUP, 'General')
    }
  }

  componentDidMount() {
    const { match, fetchApiKey } = this.props
    const { appId } = match.params

    fetchApiKey(appId)
    this.setupAccordion()
  }

  render() {
    const { app, match, location, history, hasLocationDataType } = this.props

    const { appId } = match.params

    if (!app) {
      return null
    }

    return (
      <div className="app-settings">
        <Item title="App Settings" group={GROUP} fluid itemId="General">
          <AppSettingsForm
            appId={appId}
            onSubmit={this.handleAppSettingsSubmit}
            initialValues={this.getInitialAppSettingsData()}
          />
        </Item>
        <Item title="Display Settings" group={GROUP} fluid itemId="Display">
          <DisplaySettings />
        </Item>
        <Item title="App Access" group={GROUP} fluid itemId="Access">
          <AppUsers appId={appId} location={location} history={history} />
          <Cloneable />
          <ApiKey appId={appId} />
        </Item>
        <Item title="Copy / Delete" group={GROUP} fluid itemId="CopyDelete">
          <div className="delete-app-section app-settings-section">
            <h2>Copy App</h2>
            <Button to={`/apps/${appId}/screens/copy`}>Copy {app.name}</Button>
          </div>
          <div className="delete-app-section app-settings-section">
            <h2>Delete App</h2>
            <p>
              Once deleted, all data in the app’s database will be removed. This
              cannot be undone.
            </p>
            <Button danger onClick={this.handleDelete}>
              Delete {app.name}
            </Button>
          </div>
        </Item>
        {hasLocationDataType ? (
          <Item title="API Keys" group={GROUP} fluid itemId="APIKeys">
            <ThirdPartyApiKeys
              initialValues={this.getInitialAPIKeysData()}
              onSubmit={this.handleAPIKeysSubmit}
              appId={appId}
            />
          </Item>
        ) : null}
      </div>
    )
  }
}

const withLocation = withLocationDataType(Settings)

const mapStateToProps = (state, { match }) => ({
  app: getApp(state, match.params.appId),
  apps: getApps(state),
  currentOrgId: getCurrentOrganizationId(state),
  thirdPartyApiKeys: getThirdPartyApiKeys(state, match.params.appId),
  hasTeamSwitcher: getFeatureFlag(state, 'hasTeamSwitcher'),
})

export const SettingsSub = withRouter(
  connect(mapStateToProps, {
    updateApp,
    deleteApp,
    fetchApiKey,
    deleteThirdPartyApiKey,
    createThirdPartyApiKey,
    updateThirdPartyApiKey,
    openAccordion,
  })(withLocation)
)

class SettingsPage extends Component {
  componentDidMount() {
    const { requestApp, match } = this.props
    const { appId } = match.params

    requestApp(appId)
  }

  render() {
    return (
      <Page form title="Settings">
        <h1>App Settings</h1>
        <SettingsSub />
      </Page>
    )
  }
}

export default connect(null, { requestApp })(SettingsPage)

export class SettingsPanel extends Component {
  render() {
    return (
      <div className="app-settings-panel">
        <h2 className="left-panel-title">
          <Icon type="settings" />
          {' Settings'}
        </h2>
        <SettingsSub />
      </div>
    )
  }
}
